import * as Sentry from "@sentry/react";
import type { RemoteTrack } from "livekit-client";
import { buildLiveKitVideoOptions, RECONNECT_WINDOW_MS } from "@lc/shared";
import { recoverAudioOnNextGesture } from "../audio-unlock";
import type { JoinCallbacks, KioskVideoSession, VideoTrackHandle } from "./types";

// Internal: the structural surface of LiveKit's Track that the handle needs
// (matched by RemoteTrack + LocalTrack) — not a real livekit-client type.
interface AttachableTrack {
  attach(): HTMLMediaElement;
  detach(): HTMLMediaElement[];
  mediaStreamTrack: MediaStreamTrack;
}

/**
 * Wrap a LiveKit track in the provider-agnostic handle (spec D13). attach()
 * lets the SDK create the element (it sets playsInline/autoplay itself, incl.
 * the Safari quirk), styles it to fill the container, and appends it. `mirror`
 * flips the LOCAL self-view horizontally, matching a front-camera selfie view.
 */
function liveKitHandle(track: AttachableTrack, opts?: { mirror?: boolean }): VideoTrackHandle {
  return {
    attach(container: HTMLElement) {
      const el = track.attach() as HTMLVideoElement;
      el.style.width = "100%";
      el.style.height = "100%";
      el.style.objectFit = "cover";
      if (opts?.mirror) el.style.transform = "scaleX(-1)";
      container.appendChild(el);
    },
    detach() {
      track.detach().forEach((el) => el.remove());
    },
    mediaStreamTrack: () => track.mediaStreamTrack,
  };
}

/**
 * The kiosk's video-provider adapter — implements the JoinCallbacks contract
 * (see lib/video/types.ts), chosen by the /api/video/token provider field.
 * Dynamic-imports the SDK so it's only ever pulled into the bundle when a call
 * actually starts. Connection events are translated into the kiosk's own
 * vocabulary so interpretConnectionState + App.tsx stay provider-agnostic:
 * Reconnecting -> RECONNECTING, Reconnected -> CONNECTED, Disconnected
 * (CLIENT_INITIATED, i.e. our own leave()) -> "LEAVE" (inert — a normal
 * hang-up, already handled); any other disconnect reason is terminal.
 */
export async function joinLiveKit(
  opts: { url: string; token: string } & JoinCallbacks,
): Promise<KioskVideoSession> {
  const {
    Room,
    RoomEvent,
    Track,
    DisconnectReason,
    VideoPreset,
    createLocalAudioTrack,
    createLocalVideoTrack,
  } = await import("livekit-client");

  const { roomOptions, captureOptions, simulcastLayers } = buildLiveKitVideoOptions("kiosk");
  const room = new Room({
    ...roomOptions,
    publishDefaults: {
      ...roomOptions.publishDefaults,
      // The shared module is dependency-free, so it hands the rungs over as plain
      // data; livekit-client needs real VideoPreset instances (it reads their
      // .encoding/.resolution getters when building the simulcast encodings).
      ...(simulcastLayers
        ? {
            videoSimulcastLayers: simulcastLayers.map(
              (l) => new VideoPreset(l.width, l.height, l.maxBitrate, l.maxFramerate),
            ),
          }
        : {}),
    },
  });
  let agentJoinedFired = false;
  // Agent-audio playback elements (never in the DOM — audio needs no layout);
  // kept ONLY so leave() can stop playback + drop srcObject refs.
  const remoteAudioEls: HTMLMediaElement[] = [];

  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
    // Fire "agent present" once, on the first subscribed track of ANY kind —
    // not on each track, and NOT on video alone. Gating this on video killed
    // every call from an agent whose camera was unavailable: the portal treats
    // that as survivable (independent mic/camera acquisition, "connected
    // audio-only" banner), so such an agent is genuinely present and talking,
    // but published no video, so App.tsx's 12s ANSWER_JOIN_WATCHDOG_MS — which
    // only this callback clears — tore the call down mid-conversation.
    // Presence is what the watchdog asks about, and any subscribed track
    // answers it; the empty room it exists to rescue subscribes nothing at all.
    if (!agentJoinedFired) {
      agentJoinedFired = true;
      opts.onAgentJoined();
    }
    if (track.kind === Track.Kind.Video) {
      opts.onRemoteVideo(liveKitHandle(track as unknown as AttachableTrack));
    }
    if (track.kind === Track.Kind.Audio) {
      // Audio needs no layout: the element plays without DOM insertion.
      remoteAudioEls.push(track.attach());
    }
  });

  room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
    if (!room.canPlaybackAudio) {
      Sentry.addBreadcrumb({
        category: "livekit",
        level: "warning",
        message: "remote audio autoplay blocked; recovering on next interaction",
      });
      recoverAudioOnNextGesture(() => void room.startAudio());
    }
  });

  // Mirror of the portal's grace window. A vanished agent is NOT proof the call
  // is over: ParticipantDisconnected carries no reason, and an agent whose
  // browser does a FULL reconnect leaves and rejoins — on the wire, identical
  // to a hang-up. Agents work over hotel wifi behind RustDesk, so this is the
  // likelier direction of the two, not the rarer one.
  //
  // So a disconnect only ARMS the end; an agent back inside the window disarms
  // it. A deliberate hang-up does not pay the delay — the portal sends `bye`
  // first and App.tsx acts on it at once. The window is the fallback for when a
  // bye could not be sent, which is exactly when waiting is the right call.
  let agentLeftTimer: ReturnType<typeof setTimeout> | null = null;
  const disarmAgentLeft = () => {
    if (!agentLeftTimer) return;
    clearTimeout(agentLeftTimer);
    agentLeftTimer = null;
  };
  room.on(RoomEvent.ParticipantDisconnected, () => {
    Sentry.addBreadcrumb({
      category: "livekit",
      level: "warning",
      message: "agent disconnected; waiting out the reconnect window before ending",
      data: { windowMs: RECONNECT_WINDOW_MS },
    });
    // Already counting down — a second disconnect must not restart the clock,
    // or an agent flapping faster than the window would defer the end forever.
    if (agentLeftTimer) return;
    agentLeftTimer = setTimeout(() => {
      agentLeftTimer = null;
      opts.onAgentLeft();
    }, RECONNECT_WINDOW_MS);
  });
  room.on(RoomEvent.ParticipantConnected, () => {
    if (!agentLeftTimer) return;
    Sentry.addBreadcrumb({
      category: "livekit",
      level: "info",
      message: "agent returned inside the reconnect window; call kept alive",
    });
    disarmAgentLeft();
  });
  // Breadcrumb both reconnect transitions, not just the terminal Disconnected
  // below. A FULL reconnect is what makes the remote peer see us leave and
  // rejoin, which is what trips their ParticipantDisconnected teardown — so a
  // call lost to a recovered blip looked identical, after the fact, to a normal
  // hang-up. These two are what tell them apart.
  room.on(RoomEvent.Reconnecting, () => {
    Sentry.addBreadcrumb({ category: "livekit", level: "warning", message: "room reconnecting" });
    opts.onConnectionStateChange("RECONNECTING", "CONNECTED");
  });
  room.on(RoomEvent.Reconnected, () => {
    Sentry.addBreadcrumb({ category: "livekit", level: "info", message: "room reconnected" });
    opts.onConnectionStateChange("CONNECTED", "RECONNECTING");
  });
  // Round-4 telemetry parity with the portal adapter (spec D5): quality and
  // SFU stream-state changes leave breadcrumbs so a "the video froze" report
  // has context on the kiosk side too.
  room.on(
    RoomEvent.ConnectionQualityChanged,
    (quality: string, participant?: { identity?: string }) => {
      Sentry.addBreadcrumb({
        category: "livekit",
        level: quality === "poor" || quality === "lost" ? "warning" : "info",
        message: "connection quality changed",
        data: {
          quality,
          participant:
            participant === room.localParticipant ? "local" : (participant?.identity ?? "unknown"),
        },
      });
    },
  );
  room.on(
    RoomEvent.TrackStreamStateChanged,
    (pub: { trackSid?: string }, streamState: string, participant?: { identity?: string }) => {
      Sentry.addBreadcrumb({
        category: "livekit",
        level: streamState === "paused" ? "warning" : "info",
        message: "subscribed track stream state changed",
        data: {
          streamState,
          trackSid: pub?.trackSid ?? "unknown",
          participant: participant?.identity ?? "unknown",
        },
      });
    },
  );
  room.on(RoomEvent.Disconnected, (reason?: number) => {
    const isLeave = reason === DisconnectReason.CLIENT_INITIATED;
    // Terminal-disconnect Sentry parity with the portal (spec D5). Unlike the
    // portal (leaving-flag gating for the pagehide class), the kiosk treats
    // CLIENT_INITIATED as its own leave(): it is a Guided-Access-locked tablet,
    // so the page-lifecycle self-disconnect class the portal guards against is
    // not a kiosk failure mode worth the noise of reporting every hang-up.
    if (!isLeave) {
      Sentry.captureMessage("kiosk video room disconnected unexpectedly", {
        level: "warning",
        extra: {
          reason:
            reason == null
              ? "unknown"
              : ((DisconnectReason as unknown as Record<number, string>)[reason] ?? String(reason)),
        },
      });
    }
    opts.onConnectionStateChange("DISCONNECTED", "CONNECTED", isLeave ? "LEAVE" : String(reason ?? "UNKNOWN"));
  });
  room.on(RoomEvent.DataReceived, (payload, participant) => {
    opts.onData?.(payload, participant?.identity ?? "");
  });

  await room.connect(opts.url, opts.token);

  // Mic FIRST, camera second: the camera's cold warm-up (seconds, plus a
  // permission prompt on a fresh device) must not gate the guest's voice, so
  // publish audio the instant it's ready and only then acquire video. A camera
  // failure throws out of joinLiveKit -> App's catch -> apology (the kiosk
  // REQUIRES a camera; it is the guest's face).
  const localAudio = await createLocalAudioTrack();
  await room.localParticipant.publishTrack(localAudio);
  const localVideo = await createLocalVideoTrack(captureOptions);
  await room.localParticipant.publishTrack(localVideo);

  return {
    localVideo: liveKitHandle(localVideo as unknown as AttachableTrack, { mirror: true }),
    localAudioTrack: localAudio.mediaStreamTrack,
    leave: async () => {
      // A window pending at teardown would otherwise fire onAgentLeft into a
      // torn-down screen seconds after the kiosk already went home.
      disarmAgentLeft();
      for (const el of remoteAudioEls) {
        el.pause();
        (el as HTMLMediaElement & { srcObject: unknown }).srcObject = null;
      }
      // A disconnect during a network drop can reject — session is over either way.
      try {
        await room.disconnect();
      } catch {
        /* already disconnected */
      }
    },
    sendData: (bytes, reliable) => void room.localParticipant.publishData(bytes, { reliable }),
  };
}
